/**
 * The small pieces every screen needs.
 *
 * Kept in one file rather than one file each: they are three to thirty lines
 * apiece, and a directory of thirty-line files is harder to read than this.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, api } from "../api.js";

/** A message the person can act on, or nothing. */
export function Message({ level = "info", children }) {
  if (!children) return null;
  return <div className={`msg ${level}`}>{children}</div>;
}

export function Pill({ status }) {
  return <span className={`pill ${status}`}>{status}</span>;
}

/**
 * What the panel found wrong, for one field.
 *
 * Findings arrive from the server with the field they belong to, which is what
 * lets the message sit under the input rather than in a list at the top that
 * nobody reads.
 */
export function FieldMessages({ findings, field }) {
  const mine = (findings || []).filter((f) => f.field === field);
  if (!mine.length) return null;
  return (
    <>
      {mine.map((f, i) => (
        <div key={i} className={`msg ${f.level}`}>
          {f.message}
        </div>
      ))}
    </>
  );
}

/** How long a title or description is, against what Google tends to show. */
export function Meter({ value, limit }) {
  const length = String(value || "").length;
  if (!limit) return null;
  return (
    <span className={`meter${length > limit ? " over" : ""}`}>
      {length} / {limit}
    </span>
  );
}

/** What this page will look like in a search result. */
export function SearchPreview({ url, title, description }) {
  return (
    <div className="serp">
      <div className="u">www.allbrainsclinic.com{url}</div>
      <div className="t">{title || "— no title —"}</div>
      <div className="d">{description || "— no description, so Google will invent one —"}</div>
    </div>
  );
}

/**
 * The state of the build a save just started.
 *
 * Polls rather than holding a connection open: a deploy takes two to three
 * minutes, and the answer is worth about one request every five seconds. It
 * stops polling the moment the run finishes, and when the tab is hidden.
 *
 * The preview link, once the run has succeeded, is the address the run's own
 * log says the branch was deployed at (`alias`) plus `previewPath`; `preview`
 * — the address the API computed — is only used when the log gave nothing.
 */
export function BuildStatus({ branch, preview = null, previewPath = null, live = null, onDone = null }) {
  const [state, setState] = useState({ run: null, failure: null, alias: null });
  const [checking, setChecking] = useState(true);
  const done = useRef(false);

  useEffect(() => {
    if (!branch) return undefined;
    let timer;
    let stopped = false;

    const poll = async () => {
      if (document.hidden) {
        timer = setTimeout(poll, 5000);
        return;
      }
      try {
        const next = await api.get(`build?branch=${encodeURIComponent(branch)}`);
        if (stopped) return;
        setState(next);
        if (next.run?.status === "completed") {
          setChecking(false);
          if (!done.current) {
            done.current = true;
            onDone?.(next);
          }
          return;
        }
      } catch {
        /* a failed poll is not worth a message; the next one usually works */
      }
      timer = setTimeout(poll, 5000);
    };

    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [branch, onDone]);

  if (!branch) return null;
  const run = state.run;

  if (!run || run.status !== "completed") {
    return (
      <Message level="info">
        Building{checking ? "…" : ""} This takes two to three minutes. You can leave this screen — the
        build carries on.{" "}
        {run?.url && (
          <a href={run.url} target="_blank" rel="noopener">
            Watch it
          </a>
        )}
      </Message>
    );
  }

  if (run.conclusion === "success") {
    const previewLink = state.alias ? `${state.alias}${previewPath || "/"}` : preview;
    return (
      <Message level="ok">
        Built.{" "}
        {live ? (
          <a href={live} target="_blank" rel="noopener">
            See it live
          </a>
        ) : previewLink ? (
          <a href={previewLink} target="_blank" rel="noopener">
            Open the preview
          </a>
        ) : null}
      </Message>
    );
  }

  return (
    <Message level="error">
      The build failed{state.failure?.message ? `: ${state.failure.message}` : "."} Nothing on the live
      site has changed.{" "}
      {run.url && (
        <a href={run.url} target="_blank" rel="noopener">
          The full log
        </a>
      )}
    </Message>
  );
}

/**
 * Pick a picture, or upload one.
 *
 * The resize happens in the browser, before the upload, so that a 12 MB
 * photograph off a phone never reaches the repository — the site's own build
 * makes every size it needs from the original, and what it needs is a
 * reasonable original rather than the biggest one a camera can make.
 */
export function ImageField({ value, onChange, label = "", help = "", decorative = false }) {
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const dialog = useRef(null);

  const load = useCallback(async () => {
    try {
      setImages((await api.get("media")).images);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not list the pictures.");
    }
  }, []);

  useEffect(() => {
    if (open) {
      dialog.current?.showModal();
      if (!images) load();
    } else dialog.current?.close();
  }, [open, images, load]);

  async function upload(file) {
    setBusy(true);
    setError(null);
    try {
      const resized = await shrink(file);
      const form = new FormData();
      form.append("file", resized, file.name.replace(/\.[^.]+$/, ".jpg"));
      form.append("name", file.name);
      const { image } = await api.post("media", form);
      setImages(null);
      onChange({ url: image.url, alt: value?.alt || "", fileId: null });
      setOpen(false);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not upload that.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="field">
      {label && <label>{label}</label>}
      <div className="row">
        {value?.url ? (
          <img className="thumb" style={{ width: 64, height: 64 }} src={value.url} alt="" />
        ) : (
          <div className="thumb" style={{ width: 64, height: 64 }} />
        )}
        <button type="button" onClick={() => setOpen(true)}>
          {value?.url ? "Change" : "Choose a picture"}
        </button>
        {value?.url && (
          <button type="button" className="quiet" onClick={() => onChange(null)}>
            Remove
          </button>
        )}
      </div>

      {value?.url && !decorative && (
        <div style={{ marginTop: "0.6rem" }}>
          <label htmlFor={`alt-${label}`}>What the picture shows</label>
          <input
            id={`alt-${label}`}
            value={value.alt || ""}
            placeholder="A speech therapist sitting with a child at a low table"
            onChange={(e) => onChange({ ...value, alt: e.target.value })}
          />
          <p className="help">Read aloud to anyone who cannot see it, and shown if the picture fails to load.</p>
        </div>
      )}
      {help && <p className="help">{help}</p>}

      <dialog ref={dialog} onClose={() => setOpen(false)} onCancel={() => setOpen(false)}>
        <div className="card" style={{ border: 0, boxShadow: "none" }}>
          <header className="spread">
            <h2>Pictures</h2>
            <button type="button" className="quiet" onClick={() => setOpen(false)}>
              Close
            </button>
          </header>
          <div className="body">
            <div className="row" style={{ marginBottom: "0.8rem" }}>
              <label className="btn" style={{ cursor: "pointer" }}>
                {busy ? "Uploading…" : "Upload a picture"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  disabled={busy}
                  onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                />
              </label>
              <span className="hint">Resized to 2400px and re-saved before it is uploaded.</span>
            </div>
            <Message level="error">{error}</Message>
            {!images ? (
              <div className="empty">Loading…</div>
            ) : (
              <div className="grid">
                {images.map((image) => (
                  <figure
                    key={image.name}
                    className={value?.url === image.url ? "on" : ""}
                    onClick={() => {
                      onChange({ url: image.url, alt: value?.alt || "", fileId: null });
                      setOpen(false);
                    }}
                  >
                    <img src={image.url} alt="" loading="lazy" />
                    <figcaption>{image.name}</figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>
        </div>
      </dialog>
    </div>
  );
}

/**
 * Resize and re-encode in the browser.
 *
 * 2400px on the long edge at quality 0.82 — the numbers the brief asked for,
 * and enough for every size `lib/image-pipeline.js` generates. EXIF does not
 * survive a canvas, which is the point: a photograph taken on a phone carries
 * the place it was taken.
 */
export async function shrink(file, { maxEdge = 2400, quality = 0.82 } = {}) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  // A picture that somehow came out bigger than it went in is not an
  // improvement; send the original in that case.
  return blob && blob.size < file.size ? blob : file;
}
